import React, { useEffect, useState } from "react";
import { Form, Button, Select, message, Modal, Spin, Result } from 'antd';
import { corporateLoraDeviceService } from "@iso/services";
import { DeviceRenderFactory } from "@iso/components/SensorFactory/DeviceRenderFactory";
import { PartNumberRenderFactory } from "@iso/components/SensorFactory/PartNumberRenderFactory";

// Self-service "add device" for a property whose corporate group is linked to
// LoRa organisation(s). Mirrors the admin SensorModal device + part-number
// pickers, but the supplier is fixed to LoRa and the device list is scoped to
// the corporate group's linked organisations (fetched per property).
const SUPPLIER = 'Lora AirAgri';

const AddDeviceModal = ({ visible, onCancel, propertyId, onSuccess, preloadedDevices }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (visible && propertyId) {
      form.resetFields();
      setSelectedDevice(null);
      setFields([]);

      // Reuse the list already fetched by the Sensors page when available.
      if (Array.isArray(preloadedDevices)) {
        setDeviceList(preloadedDevices);
        setFetching(false);
        return;
      }

      setDeviceList([]);
      setFetching(true);
      corporateLoraDeviceService
        .getDevices(propertyId)
        .then((res) => {
          if (res && res.code === '0000') {
            setDeviceList(res.data || []);
          } else {
            message.error((res && res.message) || 'Failed to load available devices');
          }
        })
        .finally(() => setFetching(false));
    }
  }, [visible, propertyId, form, preloadedDevices]);

  const onChangeDevice = (value) => {
    try {
      const deviceData = JSON.parse(value);
      setSelectedDevice(deviceData.id);
      form.setFieldsValue({
        sensor: { ...form.getFieldsValue().sensor, name: deviceData.name },
      });
    } catch (e) {
      setSelectedDevice(value);
    }
    setFields([]);
  };

  const renderDeviceOptions = () => {
    if (deviceList.length === 0) {
      return <Select placeholder="No devices available" allowClear size="large" disabled />;
    }
    const renderer = DeviceRenderFactory.createRenderer(SUPPLIER);
    return renderer.renderOptions(deviceList, onChangeDevice);
  };

  const renderPartNumberSection = () => {
    const renderer = PartNumberRenderFactory.createRenderer(SUPPLIER, deviceList, selectedDevice);
    return <Form.Item>{renderer.renderFormItems()}</Form.Item>;
  };

  const handleSubmit = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        let terminalId = values.sensor.terminal_id;
        try {
          terminalId = JSON.parse(values.sensor.terminal_id).id;
        } catch (e) {
          // already a plain id
        }
        const sensor = { ...values.sensor, terminal_id: terminalId };
        corporateLoraDeviceService
          .add(propertyId, sensor)
          .then((res) => {
            if (res && res.code === '0000') {
              message.success('Device added to your property');
              form.resetFields();
              if (onSuccess) onSuccess();
              onCancel();
            } else {
              message.error((res && res.message) || 'Failed to add device');
            }
          })
          .catch(() => message.error('Failed to add device. Please try again.'))
          .finally(() => setLoading(false));
      })
      .catch(() => setLoading(false));
  };

  return (
    <Modal
      open={visible}
      title="Add Device"
      onCancel={onCancel}
      maskClosable={true}
      footer={[
        <Button key="back" onClick={onCancel} className="cancel-button">
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          className="btn-success"
          onClick={handleSubmit}
          loading={loading}
          disabled={fetching || deviceList.length === 0}
        >
          Add
        </Button>,
      ]}
    >
      {fetching ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : deviceList.length === 0 ? (
        <Result
          status="info"
          subTitle="No LoRa devices are available for this property's corporate group."
        />
      ) : (
        <Form form={form} layout="horizontal" fields={fields}>
          <Form.Item
            name={['sensor', 'terminal_id']}
            label="Device"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            {renderDeviceOptions()}
          </Form.Item>

          <Form.Item name={['sensor', 'name']} style={{ display: 'none' }}>
            <input type="hidden" />
          </Form.Item>

          {renderPartNumberSection()}
        </Form>
      )}
    </Modal>
  );
};

export default AddDeviceModal;
